import React, { ReactNode } from 'react'

import { AlertLevels, ErrorType } from 'types'

export default class ErrorBoundary extends React.Component<
  ErrorBoundaryProps,
  ErrorBoundaryState
> {
  static defaultProps = {
    active: true,
    showErrors: true,
    defaultError: {
      level: AlertLevels.ERROR,
      message: 'UNKNOWN_ERROR',
    },
  }

  state = {
    error: null,
  }

  constructor(props) {
    super(props)
  }

  componentDidCatch(caught) {
    const { active, defaultError, showErrors } = this.props
    if (!active) throw caught
    let error: ErrorType = defaultError
    if (showErrors === true) {
      error = caught
    } else if (Array.isArray(showErrors)) {
      if (
        showErrors.find((show) => {
          switch (typeof caught) {
            case 'string':
              return show === caught
            case 'object':
              return (
                'message' in (show as Error) &&
                (show as Error).message === caught.message
              )
            default:
              return false
          }
        })
      ) {
        error = caught
      }
    }
    this.setState({ error })
  }

  render() {
    const { error } = this.state
    const {
      children,
      alertComponent: AlertComponent,
      wrapComponent: WrapComponent,
    } = this.props
    if (error) {
      const alert = <AlertComponent alert={error} />
      if (WrapComponent) {
        return <WrapComponent>{alert}</WrapComponent>
      }
      return alert
    }

    return children
  }
}

type ErrorBoundaryProps = {
  children: React.ReactNode
  active?: boolean
  // Which errors to specifically show
  // True for all, false for none (all default to to defaultError), or an array of errors
  showErrors?: boolean | ErrorType[]
  // Default error type for errors not covered by showErrors
  defaultError?: ErrorType
  alertComponent: React.ComponentType<{ alert: any }>
  wrapComponent?: React.ComponentType<{ children: ReactNode }>
}

type ErrorBoundaryState = {
  error: ErrorType | null
}
